import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../config/apiConfig";
import {
    ArrowLeft, Trophy, Medal, Award, Star, Info,
    TrendingUp, TrendingDown, UserPlus, Sparkles
} from "lucide-react";
import { RingLoader } from "react-spinners";

// Fixed 5-star rating so the score's meaning is stable — one star always means
// the same thing, regardless of how many children a parent has linked.
const StarRow = ({ rating }) => (
    <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((n) => (
            <Star
                key={n}
                className={`w-4 h-4 ${n <= rating ? "text-yellow-500 fill-yellow-500" : "text-gray-300"}`}
            />
        ))}
    </div>
);

const labelColour = (rating) => {
    if (rating >= 5) return "bg-emerald-50 text-emerald-700 border-emerald-200";
    if (rating >= 4) return "bg-green-50 text-green-700 border-green-200";
    if (rating >= 3) return "bg-yellow-50 text-yellow-800 border-yellow-200";
    if (rating >= 2) return "bg-orange-50 text-orange-700 border-orange-200";
    if (rating >= 1) return "bg-red-50 text-red-700 border-red-200";
    return "bg-gray-50 text-gray-600 border-gray-200";
};

const rankBadge = (rank) => {
    if (rank === 1) return <Trophy className="w-6 h-6 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />;
    if (rank === 3) return <Award className="w-6 h-6 text-orange-500" />;
    return <span className="w-6 h-6 flex items-center justify-center text-sm font-semibold text-gray-500">#{rank}</span>;
};

export default function ParentLeaderboard() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        const load = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/parents/children-leaderboard`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                });
                const data = await res.json();
                if (!res.ok) throw new Error(data.message || "Failed to load leaderboard");
                setRows(data.data || []);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const openChild = (row) => {
        localStorage.setItem("child_id", row.user_id);
        localStorage.setItem("student_id", row.user_id);
        localStorage.setItem("viewing_context", "parent");
        navigate("/child-stats");
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <RingLoader color="#2148C0" size={48} />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-8">
            <div className="max-w-5xl mx-auto">
                {/* Header */}
                <button
                    onClick={() => navigate("/parent-dashboard")}
                    className="flex items-center gap-2 text-[#2148C0] hover:text-blue-700 font-medium mb-6"
                >
                    <ArrowLeft className="w-4 h-4" /> Back to dashboard
                </button>

                <div className="bg-gradient-to-r from-[#2148C0] to-blue-600 rounded-2xl p-6 md:p-8 text-white mb-6 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                    <div className="relative">
                        <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
                            <Trophy className="w-8 h-8" /> Family Leaderboard
                        </h1>
                        <p className="text-blue-100 mt-2 text-sm md:text-base">
                            See how each of your children is performing, side by side.
                        </p>
                    </div>
                </div>

                {/* Explainer */}
                <div className="bg-white border border-gray-200 rounded-xl p-4 md:p-5 mb-6 flex gap-3">
                    <Info className="w-5 h-5 text-[#2148C0] mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-gray-600 leading-relaxed">
                        <p className="font-semibold text-gray-800 mb-1">How the rating works</p>
                        <p>
                            Every child is scored out of <strong>5 stars</strong> based on the
                            average percentage they get on their exams. A child who has hardly
                            studied in the last 30 days loses a star, so a stale high score
                            doesn't outrank an active one. Rankings update as your children
                            take more exams.
                        </p>
                    </div>
                </div>

                {/* Empty state */}
                {rows.length === 0 && (
                    <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200 p-10 text-center">
                        <UserPlus className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                        <p className="font-medium text-gray-700">No children linked yet</p>
                        <p className="text-sm text-gray-500 mt-1 mb-4">
                            Add your children from your dashboard, then come back here to see how they compare.
                        </p>
                        <button
                            onClick={() => navigate("/parent-dashboard")}
                            className="px-5 py-2.5 bg-[#2148C0] text-white font-semibold rounded-xl hover:bg-blue-700 transition"
                        >
                            Go to dashboard
                        </button>
                    </div>
                )}

                {/* Podium: top 3 with data */}
                {rows.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        {rows.slice(0, 3).map((row) => (
                            <div
                                key={row.user_id}
                                onClick={() => openChild(row)}
                                className={`cursor-pointer bg-white border rounded-2xl p-5 shadow-sm hover:shadow-md transition ${
                                    row.rank === 1 ? "border-yellow-300" :
                                    row.rank === 2 ? "border-gray-300" :
                                    row.rank === 3 ? "border-orange-300" : "border-gray-200"
                                }`}
                            >
                                <div className="flex items-center justify-between mb-3">
                                    {row.rank ? rankBadge(row.rank) : <Sparkles className="w-5 h-5 text-gray-300" />}
                                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${labelColour(row.rating)}`}>
                                        {row.label}
                                    </span>
                                </div>
                                <h3 className="text-lg font-bold text-gray-800 truncate">
                                    {row.firstName} {row.lastName || ""}
                                </h3>
                                {row.yearGroup && (
                                    <p className="text-xs text-gray-500 mb-3">{row.yearGroup}</p>
                                )}
                                <div className="flex items-baseline gap-2 mb-2">
                                    <span className="text-3xl font-extrabold text-gray-800">
                                        {row.totalAnswered ? `${row.averageScore}%` : "—"}
                                    </span>
                                    <span className="text-xs text-gray-500">avg score</span>
                                </div>
                                <StarRow rating={row.rating} />
                                <p className="text-xs text-gray-500 mt-3">
                                    {row.completedTests} exam{row.completedTests === 1 ? "" : "s"} · {row.activeDaysLast30d} active day{row.activeDaysLast30d === 1 ? "" : "s"} in the last 30
                                </p>
                            </div>
                        ))}
                    </div>
                )}

                {/* Full table */}
                {rows.length > 0 && (
                    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                        <div className="px-5 py-4 border-b border-gray-100">
                            <h3 className="font-semibold text-gray-800">All children</h3>
                            <p className="text-xs text-gray-500 mt-0.5">Tap a row to open that child's full stats.</p>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 text-gray-500 uppercase text-xs tracking-wide">
                                    <tr>
                                        <th className="px-4 py-3 text-left">Rank</th>
                                        <th className="px-4 py-3 text-left">Child</th>
                                        <th className="px-4 py-3 text-center">Rating</th>
                                        <th className="px-4 py-3 text-center">Avg score</th>
                                        <th className="px-4 py-3 text-center">Exams</th>
                                        <th className="px-4 py-3 text-center">Active days<br /><span className="text-[10px] normal-case tracking-normal">last 30</span></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => (
                                        <tr
                                            key={row.user_id}
                                            onClick={() => openChild(row)}
                                            className="cursor-pointer border-t border-gray-100 hover:bg-blue-50/50 transition"
                                        >
                                            <td className="px-4 py-3">{row.rank ? rankBadge(row.rank) : <span className="text-xs text-gray-400">—</span>}</td>
                                            <td className="px-4 py-3">
                                                <div className="font-semibold text-gray-800">
                                                    {row.firstName} {row.lastName || ""}
                                                </div>
                                                {row.yearGroup && (
                                                    <div className="text-xs text-gray-500">{row.yearGroup}</div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex flex-col items-center gap-1">
                                                    <StarRow rating={row.rating} />
                                                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${labelColour(row.rating)}`}>
                                                        {row.label}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-center font-semibold text-gray-800">
                                                {row.totalAnswered ? `${row.averageScore}%` : "—"}
                                            </td>
                                            <td className="px-4 py-3 text-center text-gray-600">{row.completedTests}</td>
                                            <td className="px-4 py-3 text-center text-gray-600">{row.activeDaysLast30d}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {error && (
                    <p className="mt-4 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>
                )}
            </div>
        </div>
    );
}
